import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface StoryCardProps {
  story: {
    id: number;
    title: string;
    author: string;
    location: string;
    category: string;
    image: string;
    alt: string;
    excerpt: string;
    date: string;
    views: number;
    shares: number;
    votes: number;
  };
  onReadMore: (id: number) => void;
  onShare: (id: number) => void;
  onVote: (id: number) => void;
}

const StoryCard: React.FC<StoryCardProps> = ({ story, onReadMore, onShare, onVote }) => {
  return (
    <div className="bg-card rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={story.image}
          alt={story.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-cta font-bold rounded-full">
            {story.category}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-headline font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-300">
          {story.title}
        </h3>

        <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
          <div className="flex items-center space-x-1">
            <Icon name="UserIcon" size={16} />
            <span>{story.author}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="MapPinIcon" size={16} />
            <span>{story.location}</span>
          </div>
        </div>

        <p className="text-foreground font-body text-sm leading-relaxed mb-4 line-clamp-3">
          {story.excerpt}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="EyeIcon" size={16} />
              <span>{story.views.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="ShareIcon" size={16} />
              <span>{story.shares}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onVote(story.id)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-muted hover:bg-primary hover:text-primary-foreground rounded-md transition-colors duration-300"
              aria-label={`Vote for ${story.title}`}
            >
              <Icon name="HeartIcon" size={16} />
              <span className="text-sm font-medium">{story.votes}</span>
            </button>

            <button
              onClick={() => onReadMore(story.id)}
              className="px-4 py-1.5 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold hover:bg-secondary transition-colors duration-300"
            >
              Read Story
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryCard;